"use client";

import { MdLocationPin } from "react-icons/md";
import { ImSpinner2 } from "react-icons/im";
import moment from "moment";

export default function BookingDetails({
  currentBooking,
  bookingFetchedByReference,
  isLoading = false,
}) {
  // Robust date/time formatting functions
  const formatDate = (dateValue, format = "MMM DD, YYYY") => {
    if (!dateValue) return "Not specified";

    try {
      // Handle different input types
      let momentDate;

      if (moment.isMoment(dateValue)) {
        momentDate = dateValue;
      } else if (dateValue instanceof Date) {
        momentDate = moment(dateValue);
      } else if (typeof dateValue === "string") {
        momentDate = moment(dateValue);
      } else if (typeof dateValue === "number") {
        momentDate = moment(dateValue);
      } else {
        return "Invalid date";
      }

      // Check if the moment object is valid
      if (!momentDate.isValid()) {
        return "Invalid date";
      }

      return momentDate.format(format);
    } catch (error) {
      console.warn("Date formatting error:", error);
      return "Invalid date";
    }
  };

  const formatTime = (timeValue) => {
    if (!timeValue) return "Not specified";

    try {
      // If it's already a formatted time string (HH:MM format), return as is
      if (typeof timeValue === "string" && /^\d{1,2}:\d{2}/.test(timeValue)) {
        return timeValue;
      }

      // Handle different input types
      let momentTime;

      if (moment.isMoment(timeValue)) {
        momentTime = timeValue;
      } else if (timeValue instanceof Date) {
        momentTime = moment(timeValue);
      } else if (typeof timeValue === "string") {
        momentTime = moment(timeValue);
      } else if (typeof timeValue === "number") {
        momentTime = moment(timeValue);
      } else {
        return "Invalid time";
      }

      // Check if the moment object is valid
      if (!momentTime.isValid()) {
        return "Invalid time";
      }

      return momentTime.format("LT");
    } catch (error) {
      console.warn("Time formatting error:", error);
      return "Invalid time";
    }
  };

  // Helper function to get payment status color
  const getPaymentStatusColor = (status) => {
    if (!status) return "text-yellow-500";
    switch (status) {
      case "Successful":
        return "text-green-500";
      case "Pending":
        return "text-yellow-500";
      default:
        return "text-red-500";
    }
  };

  // Helper function to get booking status color
  const getBookingStatusColor = (status) => {
    if (["Completed", "Ongoing", "Scheduled"].includes(status)) {
      return "text-green-500";
    }
    if (["Awaiting response", "Not yet assigned"].includes(status)) {
      return "text-yellow-500";
    }
    return "text-red-500";
  };

  // Helper function to render trip details based on booking type
  const renderTripDetails = () => {
    const {
      bookingType,
      booking,
      bookingCurrency,
      bookingTotal,
      paymentId,
      bookingStatus,
    } = currentBooking;

    const commonPaymentInfo = (
      <>
        <span className="text-sm">
          {bookingCurrency?.symbol}
          {bookingFetchedByReference?.paymentId?.gateway ===
            "Admin Dashboard" && "₦"}
          {Intl.NumberFormat("en-US", {}).format(bookingTotal)}
        </span>
        <span className="h-1 w-1 bg-shuttlelaneBlack rounded-full"></span>
        <span
          className={`text-sm ${getPaymentStatusColor(
            paymentId?.paymentStatus
          )}`}
        >
          Payment {paymentId?.paymentStatus ?? "Pending"}
        </span>
      </>
    );

    const commonStatusInfo = (
      <span className={`text-sm ${getBookingStatusColor(bookingStatus)}`}>
        {bookingStatus}
      </span>
    );

    console.log("Booking fetched by reference =>", bookingFetchedByReference);
    console.log("Booking =>", booking);

    switch (bookingType) {
      case "Airport":
        return (
          <>
            <h2 className="text-2xl font-semibold">Trip Details</h2>
            <span className="text-sm text-slate-400">
              Airport Transfer Booking
            </span>
            <div className="flex items-center gap-x-1">
              <span className="text-sm">{booking?.passengers} passengers</span>
              <span className="h-1 w-1 bg-shuttlelaneBlack rounded-full"></span>
              <span className="text-sm">{formatDate(booking?.pickupDate)}</span>
              <span className="h-1 w-1 bg-shuttlelaneBlack rounded-full"></span>
              <span className="text-sm">{formatTime(booking?.pickupTime)}</span>
            </div>
            <div className="flex items-center gap-x-1">{commonPaymentInfo}</div>
            <div className="flex items-center gap-x-1">
              <span className="text-sm">
                {bookingFetchedByReference?.booking?.vehicleClass?.className}
              </span>
              <span className="h-1 w-1 bg-shuttlelaneBlack rounded-full"></span>
              {commonStatusInfo}
            </div>
            <div className="flex flex-col">
              {booking?.returnDate && (
                <span className="text-sm">
                  <strong>Return Date:</strong>{" "}
                  {formatDate(booking?.returnDate)}
                </span>
              )}
              {booking?.returnTime && (
                <span className="text-sm">
                  <strong>Return Time:</strong>{" "}
                  {formatTime(booking?.returnTime)}
                </span>
              )}
            </div>
            {currentBooking?.hasPriorityPass && (
              <div className="flex items-center gap-x-1">
                <span className="text-sm">
                  {booking?.priorityPassCount} {booking?.priorityPassType?.name}
                </span>
              </div>
            )}
          </>
        );

      case "Car":
        return (
          <>
            <h2 className="text-2xl font-semibold">Trip Details</h2>
            <span className="text-sm text-slate-400">Car Rental Booking</span>
            <div className="flex items-center gap-x-1">
              <span className="text-sm">{booking?.days} days</span>
              <span className="h-1 w-1 bg-shuttlelaneBlack rounded-full"></span>
              <span className="text-sm">{formatDate(booking?.pickupDate)}</span>
              <span className="h-1 w-1 bg-shuttlelaneBlack rounded-full"></span>
              <span className="text-sm">{formatTime(booking?.pickupTime)}</span>
            </div>
            <div className="flex items-center gap-x-1">{commonPaymentInfo}</div>
            <div className="flex items-center gap-x-1">
              <span className="text-sm">
                {bookingFetchedByReference?.booking?.car?.name}
              </span>
              <span className="h-1 w-1 bg-shuttlelaneBlack rounded-full"></span>
              {commonStatusInfo}
            </div>
          </>
        );

      case "Priority":
        return (
          <>
            <h2 className="text-2xl font-semibold">Trip Details</h2>
            <span className="text-sm text-slate-400">
              Priority Pass Booking
            </span>
            <div className="flex items-center gap-x-1">
              <span className="text-sm">{booking?.passengers} passengers</span>
              <span className="h-1 w-1 bg-shuttlelaneBlack rounded-full"></span>
              <span className="text-sm">{formatDate(booking?.pickupDate)}</span>
              <span className="h-1 w-1 bg-shuttlelaneBlack rounded-full"></span>
              <span className="text-sm">{formatTime(booking?.pickupTime)}</span>
            </div>
            <div className="flex items-center gap-x-1">
              <span className="text-sm">
                {bookingFetchedByReference?.booking?.pass?.name}
              </span>
              <span className="h-1 w-1 bg-shuttlelaneBlack rounded-full"></span>
              {commonPaymentInfo}
            </div>
            <div className="flex items-center gap-x-1">
              <span className="text-sm">{booking?.service}</span>
              <span className="h-1 w-1 bg-shuttlelaneBlack rounded-full"></span>
              {commonStatusInfo}
            </div>
          </>
        );

      case "Visa":
        return (
          <>
            <h2 className="text-2xl font-semibold">Trip Details</h2>
            <span className="text-sm text-slate-400">
              Visa On Arrival Booking
            </span>
            <div className="flex items-center gap-x-1">
              <span className="text-sm">{booking?.airline}</span>
              <span className="h-1 w-1 bg-shuttlelaneBlack rounded-full"></span>
              <span className="text-sm">
                {formatDate(booking?.departureDate)}
              </span>
              <span className="h-1 w-1 bg-shuttlelaneBlack rounded-full"></span>
              <span className="text-sm">
                {formatDate(booking?.arrivalDate)}
              </span>
            </div>
            <div className="flex items-center gap-x-1">
              <span className="text-sm">{booking?.bookingReference}</span>
              <span className="h-1 w-1 bg-shuttlelaneBlack rounded-full"></span>
              <span className="text-sm">
                ${Intl.NumberFormat("en-US", {}).format(bookingTotal)}
              </span>
              <span className="h-1 w-1 bg-shuttlelaneBlack rounded-full"></span>
              <span
                className={`text-sm ${getPaymentStatusColor(
                  paymentId?.paymentStatus
                )}`}
              >
                Payment {paymentId?.paymentStatus ?? "Pending"}
              </span>
            </div>
            <div className="flex items-center gap-x-1">
              <span className="text-sm">{booking?.flightNumber}</span>
              <span className="h-1 w-1 bg-shuttlelaneBlack rounded-full"></span>
              {commonStatusInfo}
            </div>
          </>
        );

      default:
        return null;
    }
  };

  // Helper function to render location details
  const renderLocationDetails = () => {
    const { bookingType, booking } = currentBooking;

    switch (bookingType) {
      case "Airport":
        return (
          <div className="flex flex-col mt-4 gap-y-1">
            <div className="flex items-center gap-x-1">
              <div className="ml-1 h-4 w-4 border-[.5px] border-shuttlelaneBlack rounded-full"></div>
              <span className="text-sm">{booking?.pickupAddress}</span>
            </div>
            <div className="border-r-[1px] border-r-shuttlelanePurple h-5 w-3 border-dashed"></div>
            <div className="flex items-center gap-x-1">
              <MdLocationPin size={24} className="text-green-500" />
              <span className="text-sm">{booking?.dropoffAddress}</span>
            </div>
          </div>
        );

      case "Car":
      case "Priority":
        return (
          <div className="flex flex-col mt-4 gap-y-1">
            <div className="flex items-center gap-x-1">
              <MdLocationPin size={24} className="text-green-500" />
              <span className="text-sm">{booking?.pickupAddress}</span>
            </div>
          </div>
        );

      case "Visa":
        return (
          <div className="flex flex-col mt-4 gap-y-1">
            <div className="flex items-center gap-x-1">
              <div className="ml-1 h-4 w-4 border-[.5px] border-shuttlelaneBlack rounded-full"></div>
              <span className="text-sm">{booking?.countryOfDeparture}</span>
            </div>
            <div className="border-r-[1px] border-r-shuttlelanePurple h-5 w-3 border-dashed"></div>
            <div className="flex items-center gap-x-1">
              <MdLocationPin size={24} className="text-green-500" />
              <span className="text-sm">{booking?.portOfEntry}</span>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Helper function to render passenger details
  const renderPassengerDetails = () => {
    const { bookingType, booking, user, firstName, lastName, mobile, email } =
      currentBooking;

    const commonFields = (
      <>
        <span className="text-sm font-semibold">
          Full Name:{" "}
          <span className="text-sm font-normal">
            {user?.firstName ?? firstName} {user?.lastName ?? lastName}
          </span>
        </span>
        <span className="text-sm font-semibold">
          Phone Number:{" "}
          <span className="text-sm font-normal">{user?.mobile ?? mobile}</span>
        </span>
        <span className="text-sm font-semibold">
          Email Address:{" "}
          <span className="text-sm font-normal">{user?.email ?? email}</span>
        </span>
      </>
    );

    if (bookingType === "Visa") {
      return (
        <>
          {/* General Information */}
          <div className="mt-5">
            <h2 className="text-xl font-semibold">General Information</h2>
            <div className="flex flex-col gap-y-1">
              <span className="text-sm font-semibold">
                Nationality:{" "}
                <span className="text-sm font-normal">
                  {booking?.nationality}
                </span>
              </span>
              <span className="text-sm font-semibold">
                Class Of Visa:{" "}
                <span className="text-sm font-normal">
                  {booking?.visaClass}
                </span>
              </span>
              <span className="text-sm font-semibold">
                Passport Type:{" "}
                <span className="text-sm font-normal">
                  {booking?.passportType}
                </span>
              </span>
            </div>
          </div>

          {/* Biodata */}
          <div className="mt-5">
            <h2 className="text-xl font-semibold">Biodata</h2>
            <div className="flex flex-col gap-y-1">
              <span className="text-sm font-semibold">
                Full Name:{" "}
                <span className="text-sm font-normal">
                  {booking?.title} {booking?.surname} {booking?.middleName}{" "}
                  {booking?.firstName}
                </span>
              </span>
              <span className="text-sm font-semibold">
                Email Address:{" "}
                <span className="text-sm font-normal">{booking?.email}</span>
              </span>
              <span className="text-sm font-semibold">
                Date Of Birth:{" "}
                <span className="text-sm font-normal">
                  {formatDate(booking?.dateOfBirth)}
                </span>
              </span>
              <span className="text-sm font-semibold">
                Place Of Birth:{" "}
                <span className="text-sm font-normal">
                  {booking?.placeOfBirth}
                </span>
              </span>
              <span className="text-sm font-semibold">
                Gender:{" "}
                <span className="text-sm font-normal">{booking?.gender}</span>
              </span>
              <span className="text-sm font-semibold">
                Marital Status:{" "}
                <span className="text-sm font-normal">
                  {booking?.maritalStatus}
                </span>
              </span>
              <span className="text-sm font-semibold">
                Passport Number:{" "}
                <span className="text-sm font-normal">
                  {booking?.passportNumber}
                </span>
              </span>
              <span className="text-sm font-semibold">
                Passport Expiry Date:{" "}
                <span className="text-sm font-normal">
                  {formatDate(booking?.passportExpiryDate, "MMM DD, YY")}
                </span>
              </span>
            </div>
          </div>

          {/* Travel Information */}
          <div className="mt-5">
            <h2 className="text-xl font-semibold">Travel Information</h2>
            <div className="flex flex-col gap-y-1">
              <span className="text-sm font-semibold">
                Purpose Of Journey:{" "}
                <span className="text-sm font-normal">
                  {booking?.purposeOfJourney}
                </span>
              </span>
              <span className="text-sm font-semibold">
                Airline:{" "}
                <span className="text-sm font-normal">{booking?.airline}</span>
              </span>
              <span className="text-sm font-semibold">
                Flight Number:{" "}
                <span className="text-sm font-normal">
                  {booking?.flightNumber}
                </span>
              </span>
              <span className="text-sm font-semibold">
                Country Of Departure:{" "}
                <span className="text-sm font-normal">
                  {booking?.countryOfDeparture}
                </span>
              </span>
              <span className="text-sm font-semibold">
                Departure Date:{" "}
                <span className="text-sm font-normal">
                  {formatDate(booking?.departureDate)}
                </span>
              </span>
              <span className="text-sm font-semibold">
                Arrival Date:{" "}
                <span className="text-sm font-normal">
                  {formatDate(booking?.arrivalDate)}
                </span>
              </span>
              <span className="text-sm font-semibold">
                Port Of Entry:{" "}
                <span className="text-sm font-normal">
                  {booking?.portOfEntry}
                </span>
              </span>
              <span className="text-sm font-semibold">
                Duration Of Stay:{" "}
                <span className="text-sm font-normal">
                  {booking?.durationOfStay} days
                </span>
              </span>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mt-5">
            <h2 className="text-xl font-semibold">Contact Information</h2>
            <div className="flex flex-col gap-y-1">
              <span className="text-sm font-semibold">
                Name:{" "}
                <span className="text-sm font-normal">
                  {booking?.contactName}
                </span>
              </span>
              <span className="text-sm font-semibold">
                Phone Number:{" "}
                <span className="text-sm font-normal">
                  {booking?.contactNumber}
                </span>
              </span>
              <span className="text-sm font-semibold">
                Address:{" "}
                <span className="text-sm font-normal">
                  {booking?.contactAddress}
                </span>
              </span>
              <span className="text-sm font-semibold">
                City:{" "}
                <span className="text-sm font-normal">
                  {booking?.contactCity}
                </span>
              </span>
              <span className="text-sm font-semibold">
                State:{" "}
                <span className="text-sm font-normal">
                  {booking?.contactState}
                </span>
              </span>
              <span className="text-sm font-semibold">
                Email Address:{" "}
                <span className="text-sm font-normal">
                  {booking?.contactEmail}
                </span>
              </span>
              <span className="text-sm font-semibold">
                Postal Code:{" "}
                <span className="text-sm font-normal">
                  {booking?.contactPostalCode}
                </span>
              </span>
            </div>
          </div>
        </>
      );
    }

    return (
      <div className="mt-5">
        <h2 className="text-xl font-semibold">Passenger Details</h2>
        <div className="flex flex-col gap-y-1">
          {commonFields}
          {(bookingType === "Airport" || bookingType === "Priority") && (
            <>
              <span className="text-sm font-semibold">
                Airline:{" "}
                <span className="text-sm font-normal">{booking?.airline}</span>
              </span>
              <span className="text-sm font-semibold">
                Flight Number:{" "}
                <span className="text-sm font-normal">
                  {booking?.flightNumber}
                </span>
              </span>
            </>
          )}
        </div>
      </div>
    );
  };

  // Helper function to render priority pass details
  const renderPriorityPassDetails = () => {
    if (
      currentBooking?.bookingType !== "Airport" ||
      !currentBooking?.booking?.priorityPassType
    ) {
      return null;
    }

    return (
      <div className="mt-5">
        <h2 className="text-xl font-semibold">Priority Pass Details</h2>
        <div className="flex flex-col gap-y-1">
          <span className="text-sm font-semibold">
            Pass Type:{" "}
            <span className="text-sm font-normal">
              {bookingFetchedByReference?.booking?.priorityPassType?.name}
            </span>
          </span>
          <span className="text-sm font-semibold">
            Number of passes:{" "}
            <span className="text-sm font-normal">
              {currentBooking?.booking?.priorityPassCount}
            </span>
          </span>
        </div>
      </div>
    );
  };

  // Helper function to render driver details
  const renderDriverDetails = () => {
    if (currentBooking?.bookingType === "Visa") {
      return null;
    }

    const { assignedDriver, vendorAssignedDriver } = currentBooking;
    const driver = assignedDriver || vendorAssignedDriver;

    return (
      <div className="mt-5">
        <h2 className="text-xl font-semibold">Driver Details</h2>
        {driver ? (
          <div className="flex flex-col gap-y-1">
            <div className="flex items-center gap-x-2">
              <div className="h-16 w-16 rounded-full overflow-hidden">
                <img
                  src={driver?.image || "/placeholder.svg"}
                  alt={`${driver?.firstName} ${driver?.lastName}`}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold">
                  Full Name:{" "}
                  <span className="text-sm font-normal">
                    {driver?.firstName} {driver?.lastName}
                  </span>
                </span>
                <span className="text-sm font-semibold">
                  Email Address:{" "}
                  <span className="text-sm font-normal">{driver?.email}</span>
                </span>
                <span className="text-sm font-semibold">
                  Phone Number:{" "}
                  <span className="text-sm font-normal">{driver?.mobile}</span>
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full">
            <i>A driver has not yet been assigned to this booking</i>
          </div>
        )}
      </div>
    );
  };

  // Helper function to render car details
  const renderCarDetails = () => {
    if (currentBooking?.bookingType === "Visa") {
      return null;
    }

    const { assignedDriver, assignedCar } = currentBooking;
    const car = assignedDriver || assignedCar;

    return (
      <div className="mt-5">
        <h2 className="text-xl font-semibold">Car Details</h2>
        {car ? (
          <div className="flex flex-col gap-y-1">
            <span className="text-sm font-semibold">
              Type: <span className="text-sm font-normal">{car?.carType}</span>
            </span>
            <span className="text-sm font-semibold">
              Name: <span className="text-sm font-normal">{car?.carName}</span>
            </span>
            <span className="text-sm font-semibold">
              Model:{" "}
              <span className="text-sm font-normal">{car?.carModel}</span>
            </span>
            <span className="text-sm font-semibold">
              Year: <span className="text-sm font-normal">{car?.carYear}</span>
            </span>
            <span className="text-sm font-semibold">
              Plate Number:{" "}
              <span className="text-sm font-normal">{car?.carPlateNumber}</span>
            </span>
            <span className="text-sm font-semibold">
              Color:{" "}
              <span className="text-sm font-normal">{car?.carColor}</span>
            </span>
          </div>
        ) : (
          <div className="w-full">
            <i>A car has not yet been assigned to this booking</i>
          </div>
        )}
      </div>
    );
  };

  if (!currentBooking) {
    return null;
  }

  return (
    <div className="h-full flex flex-col gap-y-5 pb-20">
      <div className="flex items-center justify-center w-full pb-20">
        <div className="w-full mt-10 lg:p-10 p-7 border-[1.3px] border-shuttlelanePurple border-dashed">
          {renderTripDetails()}
          {renderLocationDetails()}
          {renderPassengerDetails()}
          {renderPriorityPassDetails()}
          {renderDriverDetails()}
          {renderCarDetails()}
        </div>
        {isLoading && (
          <ImSpinner2
            size={24}
            className="text-shuttlelanePurple animate-spin"
          />
        )}
      </div>
    </div>
  );
}
